import { useEffect } from 'react';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ItemsSection from './components/ItemsSection';
import ProcessSection from './components/ProcessSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://konokane.jp';

export default function Home() {
  useEffect(() => {
    const existingScript = document.querySelector('script[data-schema="home"]');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'home');
    script.textContent = JSON.stringify([
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        '@id': `${SITE_URL}/#localbusiness`,
        name: '買取こんかね',
        alternateName: '蔵合同会社',
        description:
          '北海道を拠点に催事・出張買取専門。ブランド品・貴金属・骨董品・家電・時計・楽器・アイヌ刺繍・アイヌ民芸品など幅広く買取。査定・出張費無料、現金即払い。',
        url: SITE_URL,
        logo: `${SITE_URL}/logo.png`,
        areaServed: {
          '@type': 'State',
          name: '北海道',
        },
        address: {
          '@type': 'PostalAddress',
          addressRegion: '北海道',
          addressCountry: 'JP',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 43.0642,
          longitude: 141.3469,
        },
        priceRange: '無料査定',
        currenciesAccepted: 'JPY',
        paymentAccepted: '現金, 銀行振込',
        knowsLanguage: 'ja',
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: '買取品目一覧',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'ブランド品買取' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '貴金属・宝石買取' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '骨董品・美術品買取' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'アイヌ刺繍買取' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'アイヌ民芸品買取' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '家電・カメラ買取' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '時計買取' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '楽器買取' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'コイン・切手買取' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: '古銭・古紙幣買取' } },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: '催事・出張買取サービス',
        provider: {
          '@type': 'LocalBusiness',
          name: '買取こんかね',
          '@id': `${SITE_URL}/#localbusiness`,
        },
        areaServed: {
          '@type': 'State',
          name: '北海道',
        },
        description:
          '北海道を拠点に催事会場やご自宅へ出張し、ブランド品・貴金属・骨董品・アイヌ民芸品など幅広く現金買取するサービスです。査定・出張費は完全無料。',
        serviceType: '出張買取・催事買取',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'JPY',
          description: '査定・出張費完全無料',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '出張費・査定費はかかりますか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '出張費・査定費はすべて完全無料です。お断りいただいても一切費用はかかりません。',
            },
          },
          {
            '@type': 'Question',
            name: '北海道以外でも対応していますか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '基本的に北海道を拠点に対応しておりますが、遠方の場合もお気軽にご相談ください。',
            },
          },
          {
            '@type': 'Question',
            name: 'どのような品物を買取していますか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'ブランド品・貴金属・宝石・骨董品・美術品・アイヌ刺繍・アイヌ民芸品・家電・カメラ・時計・楽器・コイン・切手・古銭・古紙幣など幅広く買取しています。',
            },
          },
          {
            '@type': 'Question',
            name: '実店舗はありますか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '実店舗はございません。催事会場やご自宅・指定場所への出張買取を専門としています。',
            },
          },
          {
            '@type': 'Question',
            name: '支払い方法は何ですか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '査定金額にご納得いただけましたら、その場で現金にてお支払いいたします。銀行振込にも対応しています。',
            },
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'ホーム',
            item: SITE_URL,
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: '買取こんかね',
        url: SITE_URL,
        description:
          '北海道を拠点に催事・出張買取専門。ブランド品・貴金属・骨董品など幅広く買取。査定・出張費無料、現金即払い。',
        inLanguage: 'ja',
        publisher: {
          '@type': 'Organization',
          name: '蔵合同会社',
          url: SITE_URL,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${SITE_URL}/#webpage`,
        url: SITE_URL,
        name: '北海道 催事出張買取｜買取こんかね｜蔵合同会社',
        description:
          '北海道を拠点に催事・出張買取専門。ブランド品・貴金属・骨董品・家電・時計・楽器など幅広く買取。査定・出張費無料、現金即払い。',
        inLanguage: 'ja',
        isPartOf: {
          '@id': `${SITE_URL}/#website`,
        },
        about: {
          '@id': `${SITE_URL}/#localbusiness`,
        },
        dateModified: '2026-03-24',
      },
    ]);
    document.head.appendChild(script);

    return () => {
      const s = document.querySelector('script[data-schema="home"]');
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-kura-cream">
      <Navbar />
      <HeroSection />
      <AboutSection />
      <ItemsSection />
      <ProcessSection />
      <ContactSection />
      <Footer />
    </div>
  );
}
